import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/practice :: ChoiceList
 *
 * ARIA radiogroup over the 2-5 answer choices of a practice question. The
 * parent (PracticeSession) owns `selectedChoiceId` so the component is
 * purely presentational — callers decide whether a pick submits or needs
 * a separate Submit click. We use `role="radiogroup"` + individual
 * `role="radio"` buttons rather than native <input type="radio"> because
 * we want full Tailwind styling + focus outlines without fighting the
 * platform's built-in indicator.
 *
 * Keyboard (matches StateSwitcher's tablist pattern):
 *   - ArrowDown / ArrowRight → focus next choice (wraps)
 *   - ArrowUp   / ArrowLeft  → focus previous choice (wraps)
 *   - Home / End             → focus first / last
 *   - Enter / Space          → select the currently focused choice
 *
 * When `disabled` is true (post-submit state), clicks + key-selects are
 * ignored and every button carries the `disabled` attribute so screen
 * readers announce it correctly.
 */
export interface ChoiceListProps {
  choices: ReadonlyArray<{ id: string; text: string }>;
  selectedChoiceId: string | null;
  onSelect: (choiceId: string) => void;
  disabled: boolean;
}

export default function ChoiceList({
  choices,
  selectedChoiceId,
  onSelect,
  disabled,
}: ChoiceListProps) {
  const { t } = useTranslation();
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  function focusAt(index: number) {
    if (choices.length === 0) return;
    const safe = ((index % choices.length) + choices.length) % choices.length;
    const target = choices[safe];
    if (!target) return;
    btnRefs.current[target.id]?.focus();
  }

  function handleKey(
    ev: KeyboardEvent<HTMLButtonElement>,
    index: number,
    id: string,
  ) {
    switch (ev.key) {
      case "ArrowDown":
      case "ArrowRight":
        ev.preventDefault();
        focusAt(index + 1);
        return;
      case "ArrowUp":
      case "ArrowLeft":
        ev.preventDefault();
        focusAt(index - 1);
        return;
      case "Home":
        ev.preventDefault();
        focusAt(0);
        return;
      case "End":
        ev.preventDefault();
        focusAt(choices.length - 1);
        return;
      case "Enter":
      case " ":
        ev.preventDefault();
        if (!disabled) onSelect(id);
        return;
      default:
        return;
    }
  }

  return (
    <div
      role="radiogroup"
      aria-label={t("practice.choices.ariaLabel")}
      data-testid="practice-choices"
      className="mt-md flex flex-col gap-sm"
    >
      {choices.map((choice, index) => {
        const selected = choice.id === selectedChoiceId;
        const className = [
          "rounded-md border px-md py-sm text-left text-base transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
          "disabled:cursor-not-allowed disabled:opacity-70",
          selected
            ? "border-brand-primary bg-brand-primary text-white"
            : "border-brand-primary/20 bg-white text-brand-primary hover:bg-brand-primary/5",
        ].join(" ");
        return (
          <button
            key={choice.id}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`practice-choice-${choice.id}`}
            data-choice-id={choice.id}
            tabIndex={selected || (!selectedChoiceId && index === 0) ? 0 : -1}
            disabled={disabled}
            ref={(el) => {
              btnRefs.current[choice.id] = el;
            }}
            onClick={() => {
              if (!disabled) onSelect(choice.id);
            }}
            onKeyDown={(ev) => handleKey(ev, index, choice.id)}
            className={className}
          >
            <span className="mr-sm inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/20 text-xs font-semibold uppercase">
              {choice.id}
            </span>
            {choice.text}
          </button>
        );
      })}
    </div>
  );
}
